import { TagsIcon, ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { Button } from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { StatusBadge } from '@/product/feedback/shared/StatusBadge'
import { TagBadges } from '@/product/feedback/shared/TagBadges'

type TicketData = RouterOutput['product']['feedback']['getTicket']

const VoteCountBadge = ({ label }: { label: string }) => {
    return (
        <span className="border-border bg-background text-foreground shadow-xs dark:border-input dark:bg-input/30 inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-sm font-medium">
            <ThumbsUpIcon className="size-4" />
            {label}
        </span>
    )
}

type ToolbarProps = {
    ticket: TicketData
    canManage: boolean
    setTicketMetadataDialogOpen: (open: boolean) => void
    canCreateComment: boolean
    isVoting: boolean
    toggleVote: () => Promise<void>
}

export const Toolbar = observer(
    ({ ticket, canManage, setTicketMetadataDialogOpen, canCreateComment, isVoting, toggleVote }: ToolbarProps) => {
        const { t } = useTranslation()

        return (
            <div className="flex flex-wrap items-center justify-between gap-3 text-base">
                <div className="flex flex-wrap items-center gap-2">
                    <span>{t('product.feedback.ticket.number', { number: ticket.number })}</span>
                    <StatusBadge status={ticket.status} size="lg" className="h-7 px-3 text-sm" />
                    <TagBadges tags={ticket.tags} size="lg" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {canManage && (
                        <Button
                            variant="outline"
                            title={t('product.feedback.ticket.metadata')}
                            onClick={() => setTicketMetadataDialogOpen(true)}
                        >
                            <TagsIcon data-icon="inline-start" />
                            {t('product.feedback.ticket.metadata')}
                        </Button>
                    )}
                    {ticket.votesCount > 0 && (
                        <VoteCountBadge label={t('product.feedback.ticket.votes', { count: ticket.votesCount })} />
                    )}
                    <Button
                        variant={ticket.votedByCurrentUser ? 'destructive' : 'default'}
                        disabled={!canCreateComment}
                        loading={isVoting}
                        onClick={() => void toggleVote()}
                    >
                        {ticket.votedByCurrentUser ? (
                            <ThumbsDownIcon data-icon="inline-start" />
                        ) : (
                            <ThumbsUpIcon data-icon="inline-start" />
                        )}
                        {t(
                            ticket.votedByCurrentUser
                                ? 'product.feedback.ticket.removeUpvoteAction'
                                : 'product.feedback.ticket.upvoteAction'
                        )}
                    </Button>
                </div>
            </div>
        )
    }
)
